import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar as CalIcon, ChevronLeft, ChevronRight, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { bizDateKey, bizLocalInputValue, formatBizDateTime, formatBizHour } from "../../lib/formatBiz";
import { Link } from "react-router-dom";

export default function CalendarView() {
  const [monthStart, setMonthStart] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [jobs, setJobs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [rescheduling, setRescheduling] = useState(null); // job object
  const [newSlot, setNewSlot] = useState("");

  const load = useCallback(() => {
    const from = new Date(monthStart);
    const to = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
    api.get("/jobs", { params: { from_iso: from.toISOString(), to_iso: to.toISOString() } }).then(r => setJobs(r.data));
  }, [monthStart]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => buildMonthGrid(monthStart), [monthStart]);
  const jobsByDay = useMemo(() => {
    const map = {};
    for (const j of jobs) {
      const key = bizDateKey(j.scheduled_at);
      if (!key) continue;
      (map[key] = map[key] || []).push(j);
    }
    return map;
  }, [jobs]);

  const label = monthStart.toLocaleString([], { month: "long", year: "numeric" });

  const doReschedule = async () => {
    if (!rescheduling || !newSlot) return;
    try {
      // newSlot comes from <input type="datetime-local"> as naive "YYYY-MM-DDTHH:MM".
      // Send as-is so the backend interprets it as business-local (America/Los_Angeles)
      // via _parse_dt_utc — avoids a browser-tz round-trip that would silently shift
      // the appointment for admins outside Pacific.
      await api.patch(`/jobs/${rescheduling.id}`, { scheduled_at: newSlot });
      toast.success("Rescheduled");
      setRescheduling(null); setNewSlot("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Calendar</h1>
          <p className="text-slate-600 mt-1">Month view · click any date to see jobs · reschedule with one tap.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="owc-btn-outline text-sm" onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></button>
          <div className="font-[Outfit] font-bold text-lg min-w-[9rem] text-center">{label}</div>
          <button className="owc-btn-outline text-sm" onClick={() => setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="owc-card p-4 overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[720px]">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="text-xs font-bold text-slate-500 uppercase text-center py-2">{d}</div>)}
          {days.map((d) => {
            const iso = d.key;
            const dayJobs = jobsByDay[iso] || [];
            const inMonth = d.month === monthStart.getMonth();
            return (
              <button
                key={iso}
                data-testid={`calendar-day-${iso}`}
                onClick={() => setSelectedDate(iso)}
                className={`min-h-[95px] p-2 text-left rounded-lg border transition-colors ${inMonth ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 text-slate-400"} ${selectedDate === iso ? "ring-2 ring-[color:var(--owc-sky)]" : "hover:border-[color:var(--owc-sky)]"}`}
              >
                <div className="text-xs font-bold">{d.day}</div>
                <div className="mt-1 space-y-1">
                  {dayJobs.slice(0, 3).map(j => (
                    <div key={j.id} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${j.status === "cancelled" ? "bg-red-50 text-red-700 line-through" : j.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>
                      {formatBizHour(j.scheduled_at)} · {j.service_name.split(" ")[0]}
                    </div>
                  ))}
                  {dayJobs.length > 3 && <div className="text-[10px] text-slate-400">+{dayJobs.length - 3} more</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="owc-card p-6">
          <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)] mb-4">Jobs on {selectedDate}</h2>
          {(jobsByDay[selectedDate] || []).length === 0 ? (
            <div className="text-slate-500 text-sm">No jobs scheduled.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {(jobsByDay[selectedDate] || []).map(j => (
                <li key={j.id} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[color:var(--owc-navy)]">{j.service_name} — <Link to={`/admin/contacts/${j.contact_id}`} className="text-[color:var(--owc-sky)] hover:underline">contact</Link></div>
                    <div className="text-xs text-slate-500">{formatBizDateTime(j.scheduled_at)} · {j.address}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs owc-badge-${j.status === "completed" ? "green" : j.status === "cancelled" ? "red" : "blue"}`}>{j.status}</span>
                    <button data-testid={`reschedule-btn-${j.id}`} className="owc-btn-outline text-xs" onClick={() => { setRescheduling(j); setNewSlot(bizLocalInputValue(j.scheduled_at)); }}>Reschedule</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {rescheduling && (
        <div data-testid="reschedule-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="owc-card-lg p-6 w-full max-w-md">
            <h3 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)] mb-3">Reschedule job</h3>
            <div className="text-sm text-slate-600 mb-4">{rescheduling.service_name}</div>
            <input data-testid="reschedule-datetime-input" type="datetime-local" className="owc-input" value={newSlot} onChange={e => setNewSlot(e.target.value)} />
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="reschedule-cancel" className="owc-btn-outline text-sm" onClick={() => setRescheduling(null)}>Cancel</button>
              <button data-testid="reschedule-save" className="owc-btn-accent text-sm" onClick={doReschedule}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function buildMonthGrid(monthStart) {
  // Build 42 cells (6 weeks) as tz-independent {year, month, day, key} tuples.
  // We anchor via Date.UTC at 12:00 UTC to sidestep browser-tz and DST edges —
  // the calendar shows business calendar days, not browser-local days.
  const y = monthStart.getFullYear();
  const m = monthStart.getMonth();
  const first = new Date(Date.UTC(y, m, 1, 12));
  const startDow = first.getUTCDay(); // 0 = Sunday
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(Date.UTC(y, m, 1 - startDow + i, 12));
    const yy = d.getUTCFullYear();
    const mm = d.getUTCMonth();
    const dd = d.getUTCDate();
    const key = `${yy}-${String(mm + 1).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
    days.push({ year: yy, month: mm, day: dd, key });
  }
  return days;
}
